(() => {
  const bindNavigation = (root = document) => {
    const toggle = root.querySelector('[data-nav-toggle]');
    const nav = root.querySelector('[data-primary-nav]');
    if (!toggle || !nav || toggle.dataset.bound === 'true') return;

    toggle.dataset.bound = 'true';
    const close = () => {
      toggle.setAttribute('aria-expanded', 'false');
      nav.classList.remove('is-open');
      document.body.classList.remove('is-nav-open');
    };

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      nav.classList.toggle('is-open', open);
      document.body.classList.toggle('is-nav-open', open);
    });

    nav.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  };

  const bindFaq = (root = document) => {
    root.querySelectorAll('[data-faq-item]').forEach((item) => {
      const button = item.querySelector('[data-faq-button]');
      if (!button || button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const nextState = !item.classList.contains('is-open');
        const list = item.closest('[data-faq-list]');
        list?.querySelectorAll('[data-faq-item]').forEach((other) => {
          other.classList.remove('is-open');
          other.querySelector('[data-faq-button]')?.setAttribute('aria-expanded', 'false');
        });
        item.classList.toggle('is-open', nextState);
        button.setAttribute('aria-expanded', String(nextState));
      });
    });
  };

  const bindVariantPrices = (root = document) => {
    root.querySelectorAll('[data-offer-variant]').forEach((select) => {
      if (select.dataset.bound === 'true') return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => {
        const selected = select.options[select.selectedIndex];
        const scope = select.closest('.offer-copy, .product-info') || document;
        const price = scope.querySelector('[data-offer-price], [data-product-price]');
        const comparePrice = scope.querySelector('[data-variant-compare]');
        if (price && selected?.dataset.price) price.textContent = selected.dataset.price;
        if (comparePrice) {
          comparePrice.textContent = selected?.dataset.comparePrice || '';
          comparePrice.hidden = !selected?.dataset.comparePrice;
        }
      });
    });

    root.querySelectorAll('[data-offer-variant-radio]').forEach((radio) => {
      if (radio.dataset.bound === 'true') return;
      radio.dataset.bound = 'true';
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        const scope = radio.closest('.offer-copy, .product-info') || document;
        const price = scope.querySelector('[data-offer-price], [data-product-price]');
        const comparePrice = scope.querySelector('[data-variant-compare]');
        if (price && radio.dataset.price) price.textContent = radio.dataset.price;
        if (comparePrice) {
          comparePrice.textContent = radio.dataset.comparePrice || '';
          comparePrice.hidden = !radio.dataset.comparePrice;
        }
      });
    });
  };

  const bindTestOrderFields = (root = document) => {
    root.querySelectorAll('[data-test-order-fields]').forEach((fields) => {
      if (fields.dataset.bound === 'true') return;
      const issue = fields.querySelector('[data-issue-input]');
      const characterCount = fields.querySelector('[data-character-count]');
      if (!issue) return;

      fields.dataset.bound = 'true';
      const updateCharacterCount = () => {
        if (characterCount) characterCount.textContent = `${issue.value.length} / ${issue.maxLength || 800}`;
      };

      issue.addEventListener('input', updateCharacterCount);
      // Keep the existing order-document integration compatible with optional details.
      issue.form?.addEventListener('formdata', (event) => {
        if (!String(event.formData.get('properties[Fehlerbild]') || '').trim()) {
          event.formData.set('properties[Fehlerbild]', 'Nicht angegeben');
        }
      });
      updateCharacterCount();
    });
  };

  const bindReveal = (root = document) => {
    const elements = [...root.querySelectorAll('[data-reveal]:not([data-reveal-bound])')];
    if (!elements.length) return;

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    elements.forEach((element) => {
      element.dataset.revealBound = 'true';
      observer.observe(element);
    });
  };

  const bindCartQuantity = (root = document) => {
    root.querySelectorAll('[data-quantity-control]').forEach((control) => {
      if (control.dataset.bound === 'true') return;
      const input = control.querySelector('input[type="number"]');
      const minus = control.querySelector('[data-quantity-minus]');
      const plus = control.querySelector('[data-quantity-plus]');
      const form = control.closest('[data-cart-form]');
      const updateButton = form?.querySelector('[data-cart-update]');
      if (!input || !minus || !plus || !form || !updateButton) return;

      control.dataset.bound = 'true';
      let submitTimer;
      const submitUpdate = () => {
        window.clearTimeout(submitTimer);
        submitTimer = window.setTimeout(() => form.requestSubmit(updateButton), 260);
      };
      const changeBy = (amount) => {
        const minimum = Number.parseInt(input.min || '0', 10);
        const current = Number.parseInt(input.value || '0', 10);
        input.value = String(Math.max(minimum, current + amount));
        submitUpdate();
      };

      minus.addEventListener('click', () => changeBy(-1));
      plus.addEventListener('click', () => changeBy(1));
      input.addEventListener('change', submitUpdate);
      // Do not let a pending quantity update race a checkout submission.
      form.addEventListener('submit', () => window.clearTimeout(submitTimer));
    });
  };

  const bindCartConsent = (root = document) => {
    const consent = root.querySelector('#CartEarlyServiceConsent');
    const error = root.querySelector('#CartConsentError');
    if (!consent || !error || consent.dataset.bound === 'true') return;
    consent.dataset.bound = 'true';
    consent.addEventListener('invalid', (event) => {
      event.preventDefault();
      error.hidden = false;
      consent.setAttribute('aria-invalid', 'true');
      consent.closest('.cart-consent')?.scrollIntoView({ block: 'center', behavior: 'instant' });
      consent.focus({ preventScroll: true });
    });
    consent.addEventListener('change', () => {
      error.hidden = consent.checked;
      consent.setAttribute('aria-invalid', String(!consent.checked));
    });
  };

  const bindHeaderSearch = (root = document) => {
    const toggle = root.querySelector('[data-search-toggle]');
    const panel = root.querySelector('[data-header-search]');
    if (!toggle || !panel || toggle.dataset.bound === 'true') return;

    const input = panel.querySelector('[data-search-input]');
    const closeButton = panel.querySelector('[data-search-close]');
    toggle.dataset.bound = 'true';

    const close = (restoreFocus = false) => {
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('is-search-open');
      if (restoreFocus) toggle.focus();
    };

    const open = () => {
      document.querySelectorAll('.language-switcher[open]').forEach((item) => item.removeAttribute('open'));
      document.querySelectorAll('[data-account-menu].is-open').forEach((item) => {
        item.classList.remove('is-open');
        item.querySelector('[data-account-panel]')?.setAttribute('hidden', '');
        item.querySelector('[data-account-toggle]')?.setAttribute('aria-expanded', 'false');
      });
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('is-search-open');
      window.requestAnimationFrame(() => input?.focus());
    };

    toggle.addEventListener('click', () => {
      if (panel.hidden) open();
      else close(true);
    });
    closeButton?.addEventListener('click', () => close(true));
    document.addEventListener('click', (event) => {
      if (panel.hidden || panel.contains(event.target) || toggle.contains(event.target)) return;
      close(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) close(true);
    });
  };

  const bindCustomerMenu = (root = document) => {
    const menu = root.querySelector('[data-account-menu]');
    const toggle = menu?.querySelector('[data-account-toggle]');
    const panel = menu?.querySelector('[data-account-panel]');
    if (!menu || !toggle || !panel || toggle.dataset.bound === 'true') return;

    toggle.dataset.bound = 'true';
    const close = (restoreFocus = false) => {
      menu.classList.remove('is-open');
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus) toggle.focus();
    };
    const open = () => {
      document.querySelectorAll('.language-switcher[open]').forEach((item) => item.removeAttribute('open'));
      const searchPanel = document.querySelector('[data-header-search]');
      const searchToggle = document.querySelector('[data-search-toggle]');
      if (searchPanel) searchPanel.hidden = true;
      searchToggle?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('is-search-open');
      menu.classList.add('is-open');
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    };

    toggle.addEventListener('click', () => {
      if (panel.hidden) open();
      else close(true);
    });
    document.addEventListener('click', (event) => {
      if (panel.hidden || menu.contains(event.target)) return;
      close(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) close(true);
    });
  };

  const initialize = (root = document) => {
    bindNavigation(root);
    bindFaq(root);
    bindVariantPrices(root);
    bindTestOrderFields(root);
    bindReveal(root);
    bindCartQuantity(root);
    bindCartConsent(root);
    bindHeaderSearch(root);
    bindCustomerMenu(root);
  };

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
